import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../ui/Icon';
import { isSafeMarkdownUrl } from '../content/MarkdownContent';
import { noteFromDOM, noteToHTML } from './note-document';
import styles from './Notes.module.css';

const blockStyles = [
  ['h1', 'Title'], ['h2', 'Heading'], ['h3', 'Subheading'], ['p', 'Body'], ['blockquote', 'Block quote'],
] as const;

export function NoteEditor({ value, onChange, disabled = false, toolbarTarget, onNavigateToTitle }: {
  value: string; onChange: (value: string) => void; disabled?: boolean;
  toolbarTarget?: HTMLElement | null; onNavigateToTitle?: () => void;
}) {
  const editor = useRef<HTMLDivElement>(null);
  const controls = useRef<HTMLDivElement>(null);
  const selection = useRef<Range | null>(null);
  const lastValue = useRef<string>();
  const composing = useRef(false);
  const menuId = useId();
  const [menu, setMenu] = useState<'format' | 'link' | null>(null);
  const [url, setURL] = useState('');
  const [active, setActive] = useState({ bold: false, italic: false, strikeThrough: false, block: 'p' });

  useLayoutEffect(() => {
    if (lastValue.current === value || !editor.current) return;
    editor.current.innerHTML = noteToHTML(value);
    lastValue.current = value;
  }, [value]);

  useEffect(() => {
    editor.current?.querySelectorAll('input').forEach(input => { input.disabled = disabled; });
  }, [disabled, value]);

  const changed = () => {
    if (!editor.current || composing.current) return;
    const next = noteFromDOM(editor.current);
    lastValue.current = next;
    onChange(next);
  };

  useEffect(() => {
    const remember = () => {
      const current = window.getSelection();
      if (!current?.rangeCount || !editor.current?.contains(current.anchorNode)) return;
      selection.current = current.getRangeAt(0).cloneRange();
      const anchor = current.anchorNode instanceof Element ? current.anchorNode : current.anchorNode?.parentElement;
      setActive({ bold: document.queryCommandState?.('bold') ?? false, italic: document.queryCommandState?.('italic') ?? false,
        strikeThrough: document.queryCommandState?.('strikeThrough') ?? false,
        block: anchor?.closest('h1,h2,h3,h4,h5,h6,p,blockquote,pre')?.tagName.toLowerCase() ?? 'p' });
    };
    document.addEventListener('selectionchange', remember);
    return () => document.removeEventListener('selectionchange', remember);
  }, []);

  useEffect(() => {
    if (!menu) return;
    const outside = (event: PointerEvent) => { if (!controls.current?.contains(event.target as Node)) setMenu(null); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [menu]);

  const restoreSelection = () => {
    const root = editor.current;
    if (!root) return;
    root.focus();
    const range = selection.current;
    if (range && root.contains(range.commonAncestorContainer)) {
      const current = window.getSelection();
      current?.removeAllRanges();
      current?.addRange(range);
    }
  };
  const command = (name: string, argument?: string) => {
    if (disabled) return;
    restoreSelection();
    // Native editing retains the browser's undo stack and IME behavior.
    document.execCommand(name, false, argument);
    changed();
  };
  const checklist = () => {
    restoreSelection();
    const current = window.getSelection();
    const anchor = current?.anchorNode instanceof Element ? current.anchorNode : current?.anchorNode?.parentElement;
    if (!anchor?.closest('li')) command('insertUnorderedList');
    const after = window.getSelection()?.anchorNode;
    const item = (after instanceof Element ? after : after?.parentElement)?.closest('li');
    if (item) {
      const existing = item.querySelector(':scope > input');
      if (existing) existing.remove();
      else {
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.contentEditable = 'false';
        checkbox.setAttribute('aria-label', 'Checklist item');
        item.prepend(checkbox);
      }
      changed();
    }
    setMenu(null);
  };

  const toolbar = <div ref={controls} className={styles.editorToolbar} role="group" aria-label="Note formatting"
      onKeyDown={event => { if (event.key === 'Escape') { setMenu(null); restoreSelection(); } }}>
      <button type="button" className={styles.formatToggle} disabled={disabled} aria-label="Text formatting"
        aria-expanded={menu === 'format'} aria-controls={menuId} onClick={() => setMenu(menu === 'format' ? null : 'format')}>Aa</button>
      <button type="button" className={styles.tool} disabled={disabled} aria-label="Checklist" title="Checklist" onClick={checklist}><Icon name="checkbox" size={20} /></button>
      <button type="button" className={styles.tool} disabled={disabled} aria-label="Insert table" title="Insert table"
        onClick={() => command('insertHTML', '<table><thead><tr><th>Column 1</th><th>Column 2</th></tr></thead><tbody><tr><td><br></td><td><br></td></tr><tr><td><br></td><td><br></td></tr></tbody></table><p><br></p>')}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M3 14h18M10 4v16" /></svg>
      </button>
      <button type="button" className={styles.tool} disabled={disabled} aria-label="Add link" title="Add link" onClick={() => { setURL(''); setMenu(menu === 'link' ? null : 'link'); }}><Icon name="link" size={19} /></button>
      {menu === 'format' && <div id={menuId} className={styles.formatMenu} role="group" aria-label="Text style">
        <div className={styles.inlineTools}>
          {(['bold', 'italic', 'strikeThrough'] as const).map((name, i) => <button key={name} type="button" className={styles.tool}
            aria-label={['Bold', 'Italic', 'Strikethrough'][i]} aria-pressed={active[name]} onClick={() => command(name)}>
            {i === 0 ? <b>B</b> : i === 1 ? <i>I</i> : <s>S</s>}
          </button>)}
        </div>
        {blockStyles.map(([tag, label]) => <button key={tag} type="button" className={styles.styleOption} data-style={tag}
          aria-pressed={active.block === tag} onClick={() => { command('formatBlock', tag); setMenu(null); }}>
          <span className={styles.styleCheck}>{active.block === tag && <Icon name="check" size={16} />}</span>{label}
        </button>)}
        <div className={styles.listTools}>
          <button type="button" onClick={() => { command('insertUnorderedList'); setMenu(null); }}>• &nbsp; Bulleted list</button>
          <button type="button" onClick={() => { command('insertOrderedList'); setMenu(null); }}>1. &nbsp; Numbered list</button>
        </div>
      </div>}
      {menu === 'link' && <div className={styles.linkMenu} role="group" aria-label="Insert link">
        <label>Link address<input autoFocus type="url" placeholder="https://…" value={url} onChange={event => setURL(event.target.value)}
          onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); if (isSafeMarkdownUrl(url)) { command('createLink', url); setMenu(null); } } }} /></label>
        <button type="button" disabled={!isSafeMarkdownUrl(url)} onClick={() => { command('createLink', url); setMenu(null); }}>Add link</button>
      </div>}
    </div>;

  return <div className={styles.editor}>
    {toolbarTarget === undefined ? toolbar : toolbarTarget ? createPortal(toolbar, toolbarTarget) : null}
    <div ref={editor} className={styles.writing} contentEditable={!disabled} suppressContentEditableWarning
      role="textbox" aria-label="Note body" aria-multiline="true" aria-disabled={disabled} data-placeholder="Start writing…" spellCheck
      onInput={changed} onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={() => { composing.current = false; changed(); }}
      onClick={event => {
        if (event.target instanceof HTMLInputElement && !disabled) changed();
        if (event.target instanceof Element && event.target.closest('a')) event.preventDefault();
      }}
      onKeyDown={event => {
        if (event.key === 'ArrowUp' && !event.shiftKey && onNavigateToTitle) {
          const current = window.getSelection();
          if (current?.isCollapsed && current.rangeCount && editor.current) {
            const before = current.getRangeAt(0).cloneRange();
            before.selectNodeContents(editor.current);
            before.setEnd(current.anchorNode!, current.anchorOffset);
            if (!before.toString()) { event.preventDefault(); onNavigateToTitle(); return; }
          }
        }
        if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
        const current = window.getSelection();
        const node = current?.anchorNode;
        const item = (node instanceof Element ? node : node?.parentElement)?.closest('li');
        if (!item?.querySelector(':scope > input')) return;
        const list = item.parentElement;
        // Let native Enter split/exit the list, then carry checklist controls forward.
        requestAnimationFrame(() => {
          list?.querySelectorAll(':scope > li').forEach(li => {
            if (li.querySelector(':scope > input')) return;
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox'; checkbox.contentEditable = 'false';
            checkbox.setAttribute('aria-label', 'Checklist item'); li.prepend(checkbox);
          });
          changed();
        });
      }}
      onPaste={event => {
        event.preventDefault();
        // Paste as text, preserving line breaks without importing foreign styles/scripts.
        command('insertText', event.clipboardData.getData('text/plain'));
      }}
      onDrop={event => { event.preventDefault(); }} />
  </div>;
}
